import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Divider,
    Tooltip,
    Dialog,
    DialogContent
} from '@mui/material';
import { useDataContext } from '../DataContext'; // Use context
import { req } from '../utils'; // Import req
import HistoryIcon from '@mui/icons-material/History';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import NoteList from '../components/NoteList';

// --- Mock Data Removed ---

// --- Add LocalStorage Key --- 
const READY_WAREHOUSE_FILTER_STORAGE_KEY = 'readyWarehouseFilter';

export default function ReadyForDeployment() {
    // Get data from context
    const { 
        machines, isLoadingMachines, machineError,
        warehouses, isLoadingWarehouses, warehouseError,
        technicians
    } = useDataContext(); 
    
    // Filter state
    const [selectedWarehouseFilter, setSelectedWarehouseFilter] = useState('all');

    // --- Drawer State --- 
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [drawerContent, setDrawerContent] = useState(null); // 'history' or 'notes'
    const [drawerData, setDrawerData] = useState(null);
    const [isLoadingDrawer, setIsLoadingDrawer] = useState(false);
    const [drawerSerialId, setDrawerSerialId] = useState(null);

    // --- Image Gallery State ---
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [galleryImages, setGalleryImages] = useState([]);
    const [galleryIndex, setGalleryIndex] = useState(0);

    // Load filter from localStorage on mount
    useEffect(() => {
        const savedFilter = localStorage.getItem(READY_WAREHOUSE_FILTER_STORAGE_KEY);
        if (savedFilter) {
            setSelectedWarehouseFilter(savedFilter);
        }
    }, []);

    // Save filter to localStorage when it changes
    useEffect(() => {
        localStorage.setItem(READY_WAREHOUSE_FILTER_STORAGE_KEY, selectedWarehouseFilter);
    }, [selectedWarehouseFilter]);

    const handleWarehouseFilterChange = (event) => {
        setSelectedWarehouseFilter(event.target.value);
    };

    // Filter machines based on status, warehouse, and is_warehouse
    const readyMachines = machines.filter(m => 
        m.machine_status === 'ready_to_deploy' &&
        m.is_warehouse === '1' &&
        (selectedWarehouseFilter === 'all' || String(m.venue_id) === String(selectedWarehouseFilter))
    );

    // Determine overall loading/error state
    const isLoading = isLoadingMachines || isLoadingWarehouses;
    const error = machineError || warehouseError;

    // --- Drawer Handlers (Adapted from MachineIntake) --- 
    const handleOpenDrawer = async (contentType, machinePk, machineSerialId) => {
        if (!machinePk) return;
        setDrawerContent(contentType);
        setIsDrawerOpen(true);
        setIsLoadingDrawer(true);
        setDrawerData(null); 
        setDrawerSerialId(machineSerialId || null);

        try {
            let data;
            if (contentType === 'history') {
                // Fetch from server using AJAX (corrected endpoint)
                const response = await req('get', `/get_machine_history/?machine_id=${machinePk}`);
                data = response;
            } else if (contentType === 'notes') {
                // Fetch notes from endpoint
                const topic = encodeURIComponent(JSON.stringify({ subject: 'machine', subject_fk: machinePk }));
                const response = await req('get', `get_machine_notes/?topic=${topic}`);
                data = response;
            }
            setDrawerData(data); 
        } catch (err) {
            console.error(`Error fetching ${contentType} for ${machinePk}:`, err);
            setDrawerData({ error: `Failed to load ${contentType}.` }); 
        } finally {
            setIsLoadingDrawer(false);
        }
    };

    const handleCloseDrawer = () => {
        setIsDrawerOpen(false);
        setDrawerContent(null); 
        setDrawerData(null);
        setDrawerSerialId(null);
    };

    const handleAssignVenue = (machinePk) => {
        // TODO: Implement assign venue modal/logic
        alert(`Assign Venue button clicked for machine PK: ${machinePk}`);
    };

    const handleOpenGallery = (images, idx) => {
        setGalleryImages(images);
        setGalleryIndex(idx);
        setGalleryOpen(true);
    };
    const handleCloseGallery = () => setGalleryOpen(false);
    const handlePrevImage = () => setGalleryIndex(i => (i > 0 ? i - 1 : galleryImages.length - 1));
    const handleNextImage = () => setGalleryIndex(i => (i < galleryImages.length - 1 ? i + 1 : 0));

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Add Filter Controls */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}> {/* Adjust grid size as needed */}
                        <FormControl fullWidth error={!!warehouseError && !isLoadingWarehouses}>
                            <InputLabel id="ready-warehouse-filter-label">Filter by Warehouse</InputLabel>
                            <Select
                                labelId="ready-warehouse-filter-label"
                                id="ready-warehouse-filter-select"
                                value={selectedWarehouseFilter}
                                label="Filter by Warehouse"
                                onChange={handleWarehouseFilterChange}
                                disabled={isLoadingWarehouses}
                            >
                                <MenuItem value="all"><em>All Warehouses</em></MenuItem>
                                {warehouses.map((wh) => (
                                    <MenuItem key={wh.venue_id} value={String(wh.venue_id)}>
                                        {wh.venue_name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {warehouseError && <Typography color="error" variant="caption">Error loading warehouses</Typography>}
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* Loading and Error Handling */}
            {isLoading && (
                 <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                     <CircularProgress />
                 </Box>
            )}
            {error && !isLoading && (
                 <Typography color="error" align="center" sx={{ my: 3 }}>{error}</Typography>
            )}
            
            {/* Machine Table */}
            {!isLoading && !error && (
                <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                    <TableContainer sx={{ maxHeight: 640 }}> 
                        <Table stickyHeader aria-label="ready machines table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Machine ID</TableCell>
                                    <TableCell>Current Warehouse</TableCell> 
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell> {/* Add Actions Header */} 
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {readyMachines.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center"> {/* Update colSpan */} 
                                            No machines match the current filter.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    readyMachines.map((machine) => (
                                        <TableRow
                                            hover
                                            key={machine.machine_id_pk} 
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell component="th" scope="row">
                                                {machine.serial_id} 
                                            </TableCell>
                                            <TableCell>{machine.venue_name}</TableCell> 
                                            <TableCell>{machine.machine_status === 'ready_to_deploy' ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CheckCircleIcon fontSize="small" sx={{ color: 'green' }} />
                                                    <Box component="span" sx={{
                                                        bgcolor: '#e8f5e9',
                                                        color: '#388e3c',
                                                        px: 1.5,
                                                        py: 0.5,
                                                        borderRadius: 2,
                                                        fontWeight: 600,
                                                        fontSize: '0.85rem',
                                                        letterSpacing: 0.5,
                                                        textTransform: 'capitalize',
                                                    }}>
                                                        Ready to Deploy
                                                    </Box>
                                                </Box>
                                            ) : (
                                                machine.machine_status
                                            )}
                                            </TableCell>
                                            {/* Add Actions Cell */}
                                            <TableCell align="right">
                                                <Tooltip title="View History">
                                                    <IconButton size="small" onClick={() => handleOpenDrawer('history', machine.machine_id_pk, machine.serial_id)}>
                                                        <HistoryIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="View Notes">
                                                    <IconButton size="small" onClick={() => handleOpenDrawer('notes', machine.machine_id_pk, machine.serial_id)}>
                                                        <NoteAltIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Assign to Venue">
                                                    <IconButton size="small" onClick={() => handleAssignVenue(machine.machine_id_pk)}>
                                                        <LocalShippingIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* --- Drawer (Copied & Adapted) --- */ } 
            <Drawer
                anchor='right'
                open={isDrawerOpen}
                onClose={handleCloseDrawer}
            >
                <Box
                    sx={{ width: 550, p: 2 }}
                    role="presentation"
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            {drawerContent === 'history' ? 'Machine History' : drawerContent === 'notes' ? 'Machine Notes' : 'Details'}
                            {drawerSerialId ? ` (${drawerSerialId})` : ''}
                        </Typography>
                        <IconButton onClick={handleCloseDrawer}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {isLoadingDrawer ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
                    ) : drawerData?.error ? (
                        <Typography color="error">{drawerData.error}</Typography>
                    ) : drawerData && Array.isArray(drawerData) && drawerContent === 'history' ? (
                        <List dense>
                            {[...drawerData].sort((a, b) => Number(a.created_time) - Number(b.created_time)).map((item, index) => {
                                let notes = item.notes;
                                if (!notes && item.information) {
                                    try {
                                        const info = JSON.parse(item.information);
                                        notes = info.notes || '';
                                    } catch {}
                                }
                                // If repair_details exists, append its notes and show extra info
                                let repairPhotos = [];
                                let checkValues = null;
                                let partsUsed = null;
                                if (item.repair_details) {
                                    try {
                                        const repairDetails = JSON.parse(item.repair_details);
                                        if (repairDetails.notes) {
                                            notes = notes ? notes + '\n' + repairDetails.notes : repairDetails.notes;
                                        }
                                        // Collect all photos
                                        repairPhotos = [
                                            ...(repairDetails.earthing_photos || []),
                                            ...(repairDetails.clean_cabinet_photos || []),
                                            ...(repairDetails.machine_complete_photos || [])
                                        ];
                                        checkValues = repairDetails.check_values || null;
                                        partsUsed = repairDetails.parts_used || null;
                                    } catch {}
                                }
                                const date = new Date(Number(item.created_time) * 1000);
                                const dateStr = date.toLocaleString();
                                let subject = item.history_category || item.special_type || '';
                                subject = subject.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                return (
                                    <ListItem key={item.history_id || index} alignItems="flex-start" divider sx={{ mb: 2, borderRadius: 2, boxShadow: 1, bgcolor: '#f5f6fa', flexDirection: 'column', alignItems: 'stretch', p: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{subject}</Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 2, whiteSpace: 'nowrap' }}>{dateStr}</Typography>
                                        </Box>
                                        {/* Photos - now above notes, Facebook style */}
                                        {item.repair_details && repairPhotos.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                                {repairPhotos.map((url, idx) => (
                                                    <Box key={idx} component="span" sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid #eee', width: 64, height: 64, display: 'inline-block', bgcolor: '#fff', cursor: 'pointer' }}
                                                        onClick={() => handleOpenGallery(repairPhotos, idx)}>
                                                        <img src={`//app.bottlecycler.com/api/warehouse/get_img/?img_url=${encodeURIComponent(url)}`} alt="repair" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                        {/* Always show notes if present */}
                                        {notes && <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 2 }}>{notes}</Typography>}
                                        {/* Show repair details if present */}
                                        {item.repair_details && (
                                            <Box sx={{ mb: 2 }}>
                                                {/* Check values */}
                                                {checkValues && (
                                                    <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                                                        {Object.entries(checkValues).map(([key, value]) => (
                                                            <Tooltip key={key} title={value ? `Checked: ${key.replace(/_/g, ' ')}` : `Not checked: ${key.replace(/_/g, ' ')}` } placement="top">
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 0.5, borderRadius: 1, bgcolor: value ? '#e8f5e9' : '#ffebee', minWidth: 110 }}>
                                                                    <Typography variant="caption" sx={{ textTransform: 'capitalize', flex: 1 }}>{key.replace(/_/g, ' ')}:</Typography>
                                                                    {value ? (
                                                                        <CheckCircleIcon fontSize="small" sx={{ color: 'green' }} />
                                                                    ) : (
                                                                        <CancelIcon fontSize="small" sx={{ color: 'red' }} />
                                                                    )}
                                                                </Box>
                                                            </Tooltip>
                                                        ))}
                                                    </Box>
                                                )}
                                                {/* Parts used */}
                                                <Box sx={{ mt: 1 }}>
                                                    {Array.isArray(partsUsed) && partsUsed.length > 0 ? (
                                                        Array.isArray(item.parts_details) && item.parts_details.length > 0 ? (
                                                            <Box>
                                                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Parts used:</Typography>
                                                                {item.parts_details.map((part, idx) => (
                                                                    <Box key={part.part_id} sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1 }}>
                                                                        {part.img_src && (
                                                                            <img src={`//app.bottlecycler.com/api/warehouse/get_img/?img_url=${encodeURIComponent(part.img_src)}`} alt={part.name} style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 4, border: '1px solid #eee', background: '#fff', marginRight: 8 }} />
                                                                        )}
                                                                        <Typography variant="body2" component="span">{part.name} (x{part.qty})</Typography>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        ) : (
                                                            <Box>
                                                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Parts used:</Typography>
                                                                {partsUsed.map((part, idx) => (
                                                                    <Typography key={part.part_id || idx} variant="body2" component="div">
                                                                        Part {part.part_id}{part.qty ? ` (x${part.qty})` : ''}
                                                                    </Typography>
                                                                ))}
                                                            </Box>
                                                        )
                                                    ) : (
                                                        <Typography variant="body2" sx={{ display: 'block' }}>No parts used</Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                                            <Typography variant="caption" color="text.secondary">{item.account_name || 'Unknown User'}</Typography>
                                        </Box>
                                    </ListItem>
                                );
                            })}
                            {drawerData.length === 0 && (
                                <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>No history found.</Typography>
                            )}
                        </List>
                    ) : drawerData && Array.isArray(drawerData) && drawerContent === 'notes' ? (
                        <NoteList
                            notes={drawerData}
                            darkMode={false}
                            tagData={{ machines, service_calls: [], venues: [], users: technicians }}
                        />
                    ) : (
                        <Typography>No data available.</Typography> 
                    )}
                </Box>
            </Drawer>

            {/* --- Image Gallery Modal --- */}
            <Dialog open={galleryOpen} onClose={handleCloseGallery} maxWidth="md" fullWidth>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                    {galleryImages.length > 0 && (
                        <>
                            <img
                                src={`//app.bottlecycler.com/api/warehouse/get_img/?img_url=${encodeURIComponent(galleryImages[galleryIndex])}`}
                                alt="enlarged"
                                style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8, marginBottom: 8 }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                <button onClick={handlePrevImage} style={{ fontSize: 24, background: 'none', border: 'none', cursor: 'pointer' }}>&lt;</button>
                                <Typography variant="caption">{galleryIndex + 1} / {galleryImages.length}</Typography>
                                <button onClick={handleNextImage} style={{ fontSize: 24, background: 'none', border: 'none', cursor: 'pointer' }}>&gt;</button>
                            </Box>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </Container>
    );
} 